import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchLinkedOrganizationUsers } from '~/api/organizations';
import type { UserRole, UserStatus } from '~/graphql/graphql';
import { useUserRoleLabels } from '~/hooks/useUserRoleLabels';
import { exportCsv, toastGraphQLError } from '~/utils';
import {
  ALL_LINKED_ORGANIZATION_USERS_CSV_FILENAME,
  allLinkedOrganizationUsersCsvRows,
  linkedOrganizationUsersCsvFilename,
  linkedOrganizationUsersCsvRows,
  type CsvUser,
} from './linkedOrganizationUsersCsv';

type UserToExport = Omit<CsvUser, 'roleLabel' | 'statusLabel'> & {
  role: UserRole;
  status: UserStatus;
};

export function useLinkedOrganizationUsersCsvExport() {
  const { t } = useTranslation(['organizations', 'users']);
  const roleLabels = useUserRoleLabels();
  const fetchLinkedOrganizationUsers = useFetchLinkedOrganizationUsers();
  const [isExportingAll, setIsExportingAll] = useState(false);
  const userColumnHeaders = [
    t('organizations.detail.table.name'),
    t('organizations.detail.table.email'),
    t('organizations.detail.table.phoneNumber'),
    t('organizations.linked.table.role'),
    t('organizations.detail.table.status'),
    t('organizations.detail.table.lastEditedBy'),
    t('organizations.detail.table.lastEditedAt'),
  ];
  const withLabels = (user: UserToExport): CsvUser => ({
    ...user,
    roleLabel: roleLabels[user.role],
    statusLabel: t(`users.status.${user.status}`, { ns: 'users' }),
  });

  const exportOrganizationUsers = (organization: {
    name: string;
    users: UserToExport[];
  }) =>
    exportCsv(
      userColumnHeaders,
      linkedOrganizationUsersCsvRows(organization.users.map(withLabels)),
      linkedOrganizationUsersCsvFilename(organization.name)
    );

  const exportAllLinkedOrganizationUsers = async () => {
    setIsExportingAll(true);
    try {
      const users = await fetchLinkedOrganizationUsers();
      exportCsv(
        [t('organizations.linked.table.organization'), ...userColumnHeaders],
        allLinkedOrganizationUsersCsvRows(
          users.map((user) => ({
            ...withLabels(user),
            organizationName: user.organization.name,
          }))
        ),
        ALL_LINKED_ORGANIZATION_USERS_CSV_FILENAME
      );
    } catch (error) {
      toastGraphQLError(error);
    } finally {
      setIsExportingAll(false);
    }
  };

  return {
    exportOrganizationUsers,
    exportAllLinkedOrganizationUsers,
    isExportingAll,
  };
}
